import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ZodError, z } from "zod";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { useFormServerSideErrorsHandler } from "../components/form/ErrorsHandler/ErrorsHandler";

export const bankingApi = {
    getBanks: (axios, params) => {
        return axios
            .get(apiUrls.bankings.banks, {
                params,
            })
            .then((response) => response.data);
    },

    getBankById: (axios, bankId) => {
        return axios.get(apiUrls.bankings.bankDetails(bankId)).then((response) => response.data);
    },

    createBank: (axios, body) => {
        return axios.post(
            apiUrls.bankings.banks,
            body,
        );
    },

    updateBank: (axios, bankId, body) => {
        return axios.patch(
            apiUrls.bankings.bankDetails(bankId),
            body,
        );
    },

    getBankings: (axios, params) => {
        return axios
            .get(apiUrls.bankings.bankings, {
                params,
            })
            .then((response) => response.data);
    },

    getBankingFilteringData: (axios, params) => {
        return axios
            .get(apiUrls.bankings.bankingFilteringData, {
                params,
            })
            .then((response) => response.data);
    },

    getBankingById: (axios, bankingId) => {
        return axios.get(apiUrls.bankings.bankingDetails(bankingId)).then((response) => response.data);
    },

    createBanking: (axios, body) => {
        return axios.post(
            apiUrls.bankings.bankings,
            body,
        );
    },

    updateBanking: (axios, bankingId, body) => {
        return axios.patch(
            apiUrls.bankings.bankingDetails(bankingId),
            body,
        );
    },

    importBankingFile(axios, { file }) {
        const formData = new FormData();
        formData.append("file", file);
        return axios
            .post(apiUrls.bankings.importBankingFile, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((response) => response.data);
    },

    importIndividualBillsFile(axios, { file, isWithdrawal}) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("is_withdrawal", isWithdrawal)
        return axios
            .post(apiUrls.bankings.importIndividualBillsFile, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((response) => response.data);
    },    

    getTransfersInvestmentBills: (axios, params) => {
        return axios
            .get(apiUrls.bankings.transferInvestmentBills, {
                params,
            })
            .then((response) => response.data);
    },

    getTransfersInvestmentBillsFilteringData: (axios, params) => {
        return axios
            .get(apiUrls.bankings.transferInvestmentBillsFilteringData, {
                params,
            })
            .then((response) => response.data);
    },

    getInvestmentTransfersByAgreementId: (axios, agreementId) => {
        return axios.get(apiUrls.bankings.transferInvestmentForAgreementBills(agreementId)).then((response) => response.data);
    },

    massCreateReservationTransfers: (axios, agreementId, body) => {
        return axios.post(
            apiUrls.bankings.transferInvestmentBillMassCreate(agreementId),
            body,
        );
    },

    returnReservationTransfersForAgreement: (axios, agreementId, body) => {
        return axios.post(
            apiUrls.bankings.transferInvestmentBillReturnTransferForAgreement(agreementId),
            body,
        );
    },

    getTransfersIndividualBills: (axios, params) => {
        return axios
            .get(apiUrls.bankings.transferIndividualBills, {
                params,
            })
            .then((response) => response.data);
    },

    getTransfersIndividualBillsFilteringData: (axios, params) => {
        return axios
            .get(apiUrls.bankings.transferIndividualBillsFilteringData, {
                params,
            })
            .then((response) => response.data);
    },

    updateTransferIndividualBill: (axios, transferId, body) => {
        return axios.patch(
            apiUrls.bankings.transferIndividualBillDetails(transferId),
            body,
        );
    },

    getInvoices: (axios, params) => {
        return axios
            .get(apiUrls.bankings.invoices, {
                params,
            })
            .then((response) => response.data);
    },

    updateInvoice: (axios, invoiceId, body) => {
        return axios.patch(
            apiUrls.bankings.invoceDetails(invoiceId),
            body,
        );
    },

    updateInvoiceIsBooked: (axios, body) => {
        return axios.post(
            apiUrls.bankings.invoiceIsBooked,
            body,
        );
    },

    deleteMassInvoice: (axios, body) => {
        return axios.post(
            apiUrls.bankings.invoiceMassDelete,
            body,
        );
    },

    updateTransferIsBooked: (axios, body) => {
        return axios.post(
            apiUrls.bankings.transferIsBooked,
            body,
        );
    },

    updateTransferIsInvoiced: (axios, body) => {
        return axios.post(
            apiUrls.bankings.transferIsInvoiced,
            body,
        );
    },

    importInvoiceFile(axios, { file }) {
        const formData = new FormData();
        formData.append("file", file);
        return axios
            .post(apiUrls.bankings.importInvoiceFile, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((response) => response.data);
    },

};

export function useBanks(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["banks", Object.fromEntries(params.entries())],
        queryFn: () => {
            return bankingApi.getBanks(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useBankById(bankId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["banks", bankId],
        queryFn: () => {
            return bankingApi.getBankById(axiosPriv, bankId);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useCreateBankMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["banks"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ data }) => bankingApi.createBank(axiosPriv, data),
        onError: onServerError,
    });
}

export function useUpdateBankMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["banks"] });
            await queryClient.invalidateQueries({ queryKey: ["banks", variables.id] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => bankingApi.updateBank(axiosPriv, id, data),
        onError: onServerError,
    });
}

export function useBankings(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["bankings", Object.fromEntries(params.entries())],
        queryFn: () => {
            return bankingApi.getBankings(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useBankingFilteringData(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["bankings-filtering-data", Object.fromEntries(params.entries())],
        queryFn: () => {
            return bankingApi.getBankingFilteringData(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useBankingById(bankingId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["bankings", bankingId],
        queryFn: () => {
            return bankingApi.getBankingById(axiosPriv, bankingId);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useCreateBankingMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["bankings"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ data }) => bankingApi.createBanking(axiosPriv, data),
        onError: onServerError,
    });
}

export function useUpdateBankingMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["bankings"] });
            await queryClient.invalidateQueries({ queryKey: ["bankings", variables.id] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => bankingApi.updateBanking(axiosPriv, id, data),
        onError: onServerError,
    });
}


export function useBankingImportFileMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["bankings"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ file }) => {
            return bankingApi.importBankingFile(axiosPriv, { file });
        },
        onError: onServerError,
    });
}

export function useTransfersInvestmentBills(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["transfers-current-bills", Object.fromEntries(params.entries())],
        queryFn: () => {
            return bankingApi.getTransfersInvestmentBills(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useTransfersInvestmentBillsFilteringData(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["transfers-current-bills-filtering-data", Object.fromEntries(params.entries())],
        queryFn: () => {
            return bankingApi.getTransfersInvestmentBillsFilteringData(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}


export function useTransfersInvestmentBillsByAgreementId(agreementId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["transfers-current-bills", agreementId],
        queryFn: () => {
            return bankingApi.getInvestmentTransfersByAgreementId(axiosPriv, agreementId);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useMassCreateInvestmentTransferMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["transfers-current-bills"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => bankingApi.massCreateReservationTransfers(axiosPriv, id, data),
        onError: onServerError,
    });
}

export function useRetrunInvestmentTransferForAgreementMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["transfers-current-bills"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => bankingApi.returnReservationTransfersForAgreement(axiosPriv, id, data),
        onError: onServerError,
    });
}

export function useTransfersIndividualBills(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["transfers-individual-bills", Object.fromEntries(params.entries())],
        queryFn: () => {
            return bankingApi.getTransfersIndividualBills(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useTransfersIndividualBillsFilteringData(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["transfers-individual-bills-filtering-data", Object.fromEntries(params.entries())],
        queryFn: () => {
            return bankingApi.getTransfersIndividualBillsFilteringData(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useIndividualBillsImportFileMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["transfers-individual-bills"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ file, isWithdrawal }) => {
            return bankingApi.importIndividualBillsFile(axiosPriv, { file, isWithdrawal });
        },
        onError: onServerError,
    });
}

export function useUpdateTransferIndividualBillMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["transfers-individual-bills"] });
            await queryClient.invalidateQueries({ queryKey: ["transfers-individual-bills", variables.id] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => bankingApi.updateTransferIndividualBill(axiosPriv, id, data),
        onError: onServerError,
    });
}

export function useInvoices(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["invoices", Object.fromEntries(params.entries())],
        queryFn: () => {
            return bankingApi.getInvoices(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useInvoiceImportFileMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["invoices"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ file }) => {
            return bankingApi.importInvoiceFile(axiosPriv, { file });
        },
        onError: onServerError,
    });
}

export function useUpdateInvoiceMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["invoices"] });
            // await queryClient.invalidateQueries({ queryKey: ["invoices", variables.id] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ id, data }) => bankingApi.updateInvoice(axiosPriv, id, data),
        onError: onServerError,
    });
}

export function useUpdateInvoiceIsBookedMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["invoices"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ( data ) => bankingApi.updateInvoiceIsBooked(axiosPriv, data),
        onError: onServerError,
    });
}

export function useDeleteMassInvoiceMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["invoices"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ( data ) => bankingApi.deleteMassInvoice(axiosPriv, data),
        onError: onServerError,
    });
}

export function useUpdateTransferIsBookedMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["transfers-individual-bills"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ( data ) => bankingApi.updateTransferIsBooked(axiosPriv, data),
        onError: onServerError,
    });
}

export function useUpdateTransferIsInvoicedMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["transfers-current-bills"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ( data ) => bankingApi.updateTransferIsInvoiced(axiosPriv, data),
        onError: onServerError,
    });
}




